import { useQuery } from '@apollo/client';
import AutoCompleteBrands from '@components/AutoComplete/AutoCompleteBrands';
import AutoCompletePro from '@components/AutoComplete/AutoCompletePro';
import { AutoCompleteTalentProfessions } from '@components/AutoComplete/AutoCompleteTalentProfessions';
import InputAutocompleteCity from '@components/InputAutocompleteCity';
import InputDate from '@components/InputDate';
import InputFieldControlled from '@components/InputFieldControlled';
import InputSwitchControl from '@components/InputSwitchControl';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useAuth } from '@contexts/AuthContext';
import { GET_BRAND } from '@gql/brand';
import { GET_PRO } from '@gql/pros';
import { useAddJobOfferDraft } from '@modules/JobOffers/hooks/useAddJobOfferDraft';
import InputMultiSelect from '@components/InputMultiSelect';
import { useJobOfferCredits } from '@modules/subscriptionPlan/useJobOfferCredits';
import PremiumOfferDialog from '@modules/subscriptionPlan/PremiumOfferDialog';
import OptionItems, { OptionItem, permiumOptions, PremiumOption } from '@modules/subscriptionPlan/PremiumOptions';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormGroup,
    FormLabel,
    Grid,
    Stack,
    TextField,
} from '@mui/material';
import AssignJobOfferButton from './AssignJobOfferButton';
import { convertLegacyToAssigneeInput } from '../utils/assignee-utils';
import { styled, useTheme } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { moment } from '@utils/date.utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useOptionsContext } from '@modules/talents/context/options-context';
import useMediaQuery from '@mui/material/useMediaQuery';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '20px',
    paddingBottom: theme.spacing(1),
}));

interface JobOfferCreateDialogFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (jobNumber: string) => void;
    forcedProId?: string;
}

const defaultValues = {
    proId: null as string | null,
    brandId: null,
    title: '',
    city: '',
    postalCode: '',
    address: '',
    offre_cachee: false,
    enablePublishDate: false,
    publishDate: null,
    showPhotos: false,
    showVideo: false,
    featureId: null as string | null,
    assignees: [] as any[],
    recruitment: {
        method: null as string | null,
        premiumFeatures: [] as string[],
    },
    position: {
        companyName: false,
        professions: [],
        numberOfPositions: 1,
    },
};

const JobOfferCreateDialogForm: React.FC<JobOfferCreateDialogFormProps> = ({
    open,
    onClose,
    onSuccess,
    forcedProId,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { addDraft, loading } = useAddJobOfferDraft();
    const { data: optionsData } = useOptionsContext();
    const options = useMemo(() => optionsData?.options, [optionsData]);

    const [proId, setProId] = useState(forcedProId ?? user?.adminProId);

    const { data: proData } = useQuery(GET_PRO, {
        variables: { proId },
        skip: !proId,
        fetchPolicy: 'cache-first',
    });
    const pro = proData?.getPro;

    const methods = useForm({ defaultValues });
    const { control, handleSubmit, setValue, reset, watch, register } = methods;

    const { credits } = useJobOfferCredits(proId);
    const hasAvailableCredits =
        credits.length > 0 &&
        credits.some(c => c.configuration.maximum === -1 || c.configuration.used < c.configuration.maximum);

    const [showInfo, setShowInfo] = useState(false);
    const [enablePublishDate, setEnablePublishDate] = useState(false);
    const [offerPremiumOpen, setOfferPremiumOpen] = useState(false);
    const [selectPremiumOptionOpen, setSelectPremiumOptionOpen] = useState(false);

    const mainPremiumOption: PremiumOption = {
        name: 'Offre Premium',
        description: `Vous avez choisi de vous faire épauler par les équipes d'Upercut pour recruter votre futur Talent.`,
        price: {
            percentage: parseFloat(((pro?.RBA_percentage ?? 0) * 100).toFixed(2)),
            description: 'Du salaire brut annuel',
        },
    };

    const disabledPremiumOptions = useMemo(() => {
        const features: string[] = [];
        if (pro?.config?.dismissalGuarantee) {
            features.push('dismissalGuarantee');
        }
        if (pro?.config?.resignationGuarantee) {
            features.push('resignationGuarantee');
        }
        return features;
    }, [pro?.config?.dismissalGuarantee, pro?.config?.resignationGuarantee]);

    useEffect(() => {
        if (credits.length === 1) {
            setValue('featureId', credits[0]._id);
        }
    }, [credits, setValue]);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (proId) {
            setValue('proId', proId);
        }
    }, [open, proId, setValue]);

    const formProId = watch('proId');
    const { data: selectedProData } = useQuery(GET_PRO, {
        variables: { proId: formProId },
        skip: !formProId,
        fetchPolicy: 'cache-first',
    });

    useEffect(() => {
        if (!formProId) return;
        const selectedPro = selectedProData?.getPro;
        if (selectedPro) {
            setValue('city', selectedPro.city ?? '');
            setValue('postalCode', selectedPro.postal ?? '');
            setValue('address', selectedPro.address ?? '');
            setValue('position.numberOfPositions', 1);
            setValue('brandId', null);
        }
    }, [selectedProData, formProId, setValue]);

    const brandId = watch('brandId');
    const { data: brandData } = useQuery(GET_BRAND, {
        variables: { _id: brandId },
        skip: !brandId,
    });

    const brandPhotos =
        brandData?.brandPhotos?.length > 0 ? brandData?.brandPhotos : pro?.photos?.map((url: string) => ({ url }));
    const brandVideos = brandData?.brandVideos ?? pro?.brandVideos;

    const handleChangeInfo = useCallback(
        (val: boolean) => {
            setShowInfo(val);
            const hasPhotos = brandPhotos?.length > 0;
            setValue('showPhotos', val && hasPhotos);
            setValue('showVideo', val && !!brandVideos);
        },
        [brandPhotos, brandVideos, setValue],
    );

    const handleClose = () => {
        reset(defaultValues);
        setShowInfo(false);
        setEnablePublishDate(false);
        setOfferPremiumOpen(false);
        setSelectPremiumOptionOpen(false);
        onClose();
    };

    const onSubmit = async (data: any) => {
        try {
            const payload = {
                title: data.title,
                brandId: data.brandId || undefined,
                city: data.city,
                postalCode: data.postalCode || undefined,
                address: data.address || undefined,
                offre_cachee: data.offre_cachee ?? false,
                enablePublishDate: data.enablePublishDate ?? false,
                publishDate: data.enablePublishDate ? data.publishDate : null,
                showPhotos: data.showPhotos ?? false,
                showVideo: data.showVideo ?? false,
                position: {
                    companyName: data.position?.companyName ?? false,
                    professions: data.position?.professions ?? [],
                    numberOfPositions: data.position?.numberOfPositions ? parseInt(data.position.numberOfPositions) : 1,
                },
                featureId: data.featureId || undefined,
                recruitment: data.recruitment?.method
                    ? {
                          method: data.recruitment.method,
                          premiumFeatures:
                              data.recruitment.method === 'premium' ? data.recruitment?.premiumFeatures ?? [] : [],
                      }
                    : undefined,
                assignees: data.assignees?.length > 0 ? convertLegacyToAssigneeInput(data.assignees) : undefined,
            };

            const jobOffer = await addDraft(payload, data.proId || undefined);
            if (jobOffer?.jobNumber) {
                showSnackbar('Brouillon créé avec succès', 'success');
                reset(defaultValues);
                setShowInfo(false);
                setEnablePublishDate(false);
                setOfferPremiumOpen(false);
                setSelectPremiumOptionOpen(false);
                onSuccess(jobOffer.jobNumber);
            }
        } catch (err) {
            showSnackbar(err?.message ?? 'Une erreur est survenue', 'error');
        }
    };

    return (
        <FormProvider {...methods}>
            <Dialog
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={fullScreen}
                scroll="paper"
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: fullScreen ? '100dvh' : 'calc(100dvh - 32px)',
                    },
                }}>
                <StyledDialogTitle>Nouvelle offre d'emploi</StyledDialogTitle>
                <Divider />
                <DialogContent sx={{ overflowY: 'auto' }}>
                    <Grid container spacing={2.5}>
                        <Grid item xs={6}>
                            <Controller
                                name="proId"
                                control={control}
                                render={({ field }) => (
                                    <FormGroup>
                                        <FormLabel>Pour quel établissement ? </FormLabel>
                                        {forcedProId ? (
                                            <TextField
                                                value={selectedProData?.getPro?.commercialName || ''}
                                                disabled
                                                fullWidth
                                                placeholder="Chargement de l'établissement..."
                                                helperText={selectedProData?.getPro?.email || ''}
                                            />
                                        ) : (
                                            <AutoCompletePro
                                                onChange={id => {
                                                    field.onChange(id);
                                                    setProId(id);
                                                }}
                                            />
                                        )}
                                    </FormGroup>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <AutoCompleteBrands
                                name="brandId"
                                control={control}
                                label="Marque / Enseigne"
                                proId={proId}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <InputFieldControlled
                                name="title"
                                control={control}
                                label="Titre du poste"
                                placeholder="Saisissez le titre du poste"
                                required
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <InputAutocompleteCity
                                name="city"
                                label="Ville"
                                placeholder="Ex: Paris"
                                disabled={!pro?.isGroup}
                                required
                                onSelectProp={(city: string, postalCode: string) => {
                                    setValue('postalCode', postalCode);
                                }}
                            />
                            <input hidden name="postalCode" {...register('postalCode')} />
                        </Grid>

                        <Grid item xs={6}>
                            <InputFieldControlled
                                name="address"
                                control={control}
                                disabled={!pro?.isGroup}
                                label="Adresse"
                                placeholder="Ex: 12 rue de la paix"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <InputSwitchControl
                                control={control}
                                name="offre_cachee"
                                defaultValue={false}
                                title="Offre confidentielle"
                                style={{ alignItems: 'center' }}
                                onChange={(val: boolean) => {
                                    if (!val) setValue('offre_cachee', false);
                                }}
                                endAdornment={undefined}
                                disabled={undefined}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <InputSwitchControl
                                control={control}
                                name="position.companyName"
                                defaultValue={false}
                                title="Afficher le nom de l'enseigne"
                                style={{ alignItems: 'center' }}
                                onChange={handleChangeInfo}
                                endAdornment={undefined}
                                disabled={undefined}
                            />
                        </Grid>

                        {showInfo && (
                            <>
                                <Grid item xs={6}>
                                    <InputSwitchControl
                                        control={control}
                                        name="showPhotos"
                                        defaultValue={false}
                                        title="Afficher les photos de l'enseigne"
                                        disabled={!brandPhotos?.length}
                                        onChange={undefined}
                                        endAdornment={undefined}
                                        style={undefined}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <InputSwitchControl
                                        control={control}
                                        name="showVideo"
                                        defaultValue={false}
                                        title="Ajouter vos vidéos de présentation"
                                        disabled={!brandVideos}
                                        onChange={undefined}
                                        endAdornment={undefined}
                                        style={undefined}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <InputSwitchControl
                                control={control}
                                name="enablePublishDate"
                                defaultValue={false}
                                title="Date de publication"
                                style={{ alignItems: 'center' }}
                                onChange={(val: boolean) => {
                                    setEnablePublishDate(val);
                                    if (!val) setValue('publishDate', null);
                                }}
                                endAdornment={undefined}
                                disabled={undefined}
                            />
                        </Grid>

                        {enablePublishDate && (
                            <Grid item xs={12}>
                                <InputDate
                                    placeholder="ex : Jeudi 12 janvier 2023"
                                    initialDate={new Date()}
                                    minDate={moment(new Date())}
                                    name="publishDate"
                                    defaultValue={null}
                                    control={control}
                                    rules={{
                                        requiredIfTrue: (val: any) => (enablePublishDate ? !!val : true),
                                    }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={6}>
                            <AutoCompleteTalentProfessions
                                name="position.professions"
                                control={control}
                                groupedOptions={options?.groupProfessions}
                                label="Fonctions"
                                multiple
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <InputFieldControlled
                                fullWidth
                                name="position.numberOfPositions"
                                disabled={!pro?.isGroup}
                                control={control}
                                label="Nombre de postes disponibles"
                                placeholder="1"
                                type="number"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <InputMultiSelect
                                single
                                label="Quels crédits souhaitez-vous utiliser pour cette offre d'emploi ?"
                                placeholder="Choisir un crédit"
                                notClearable
                                items={[
                                    {
                                        id: '',
                                        name: 'Choisir un crédit',
                                        children: credits.map(groupOption => ({
                                            id: groupOption._id,
                                            disabled:
                                                groupOption.configuration.used >= groupOption.configuration.maximum,
                                            name: `${groupOption.planName} (${groupOption.configuration.used ?? 0} / ${
                                                groupOption.configuration.maximum === -1
                                                    ? '∞'
                                                    : groupOption.configuration.maximum
                                            } crédits)`,
                                        })),
                                    },
                                ]}
                                required
                                disabled={credits.length <= 1}
                                control={control}
                                name="featureId"
                            />
                            {credits.length === 0 && (
                                <Typography variant="caption" color="error">
                                    Aucun crédit disponible. Vous ne pouvez pas créer d'offre d'emploi.
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <InputMultiSelect
                                single
                                label="Méthode de recrutement"
                                placeholder="Choisir une méthode de recrutement"
                                notClearable
                                items={options?.recruitmentMethods ?? []}
                                required
                                control={control}
                                name="recruitment.method"
                                rules={{ required: 'Ce champ est requis' }}
                                onChange={(value: string) => {
                                    if (value === 'direct_email') {
                                        setOfferPremiumOpen(true);
                                    } else {
                                        setSelectPremiumOptionOpen(true);
                                    }
                                }}
                            />
                            <PremiumOfferDialog
                                open={offerPremiumOpen}
                                onClose={(e: any, reason: any) => {
                                    if (reason === 'backdropClick') {
                                        return;
                                    }
                                    setOfferPremiumOpen(false);
                                }}
                                onSkip={() => {
                                    setOfferPremiumOpen(false);
                                }}
                                onConfirm={() => {
                                    setValue('recruitment.method', 'premium');
                                    setOfferPremiumOpen(false);
                                    setSelectPremiumOptionOpen(true);
                                }}
                            />
                        </Grid>

                        {watch('recruitment.method') === 'premium' && (
                            <Grid item xs={12}>
                                <Controller
                                    name="recruitment.premiumFeatures"
                                    control={control}
                                    render={({ field: { value, onChange } }) => {
                                        const selectedPremiumOptions = permiumOptions.filter(option =>
                                            value?.includes(option.type),
                                        );
                                        return (
                                            <Stack gap={2}>
                                                {selectedPremiumOptions?.length > 0 && (
                                                    <Stack spacing={1}>
                                                        {[mainPremiumOption, ...selectedPremiumOptions].map(
                                                            (option, index) => (
                                                                <OptionItem
                                                                    onChange={() => {
                                                                        setSelectPremiumOptionOpen(true);
                                                                    }}
                                                                    key={index}
                                                                    data={option}
                                                                    isSelected
                                                                />
                                                            ),
                                                        )}
                                                    </Stack>
                                                )}
                                                <OptionItems
                                                    selected={value ?? []}
                                                    premiumOptionsRequired={
                                                        pro?.config?.recruitmentMethod === 'premium'
                                                    }
                                                    disabledOptions={disabledPremiumOptions}
                                                    onClose={() => {
                                                        setSelectPremiumOptionOpen(false);
                                                    }}
                                                    open={selectPremiumOptionOpen}
                                                    onChange={selectedPremiumIds => {
                                                        if (selectedPremiumIds.length === 0) {
                                                            setSelectPremiumOptionOpen(false);
                                                            setValue('recruitment.method', 'direct_email');
                                                        }
                                                        onChange(selectedPremiumIds);
                                                        setSelectPremiumOptionOpen(false);
                                                    }}
                                                />
                                            </Stack>
                                        );
                                    }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Controller
                                name="assignees"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Stack>
                                        <AssignJobOfferButton value={value} onChange={onChange} proId={proId} />
                                    </Stack>
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ gap: 1 }}>
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ borderRadius: '24px', minWidth: 100 }}>
                        Annuler
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !hasAvailableCredits}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        sx={{ borderRadius: '24px', minWidth: 120 }}>
                        Créer
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    );
};

export default JobOfferCreateDialogForm;
